import { computed, ref } from 'vue'
import { useDocumentVisibility, useIdle, useIntervalFn } from '@vueuse/core'
import SessionService from '@/services/session.service'

const service = new SessionService()

/** Never more than one heartbeat a minute, however busy the user is. */
const HEARTBEAT_INTERVAL_MS = 60_000

/** The idle clock only counts interaction within this window as "still here". */
const IDLE_AFTER_MS = 60_000

/** When the quiet top-bar countdown appears, ahead of the dialog. */
const COUNTDOWN_VISIBLE_FROM = 5 * 60

// Module-level so every consumer shares one countdown, in the same spirit as
// SettingsStore. A per-component copy would tick independently and disagree.
const secondsRemaining = ref<number | null>(null)
const warningSeconds = ref(120)
const isWarning = ref(false)
const isRenewing = ref(false)
const hasExpired = ref(false)

let lastHeartbeatAt = 0
let started = false
let warningThresholdLoaded = false

/**
 * Seed the countdown from the X-Session-Expires-In header that the API puts on
 * every authenticated response. Riding real responses means the countdown costs
 * no extra requests and cannot drift far.
 */
export const seedSessionCountdown = (seconds: number) => {
  secondsRemaining.value = seconds
  hasExpired.value = false

  // The lead time is a tenant setting, so it has to be fetched once before the
  // warning can fire at the right moment. Without this the client would sit on
  // its built-in default and warn at the wrong time.
  if (!warningThresholdLoaded) {
    warningThresholdLoaded = true

    service
      .status()
      .then((response) => {
        if (response?.data?.warningMinutes) {
          warningSeconds.value = response.data.warningMinutes * 60
        }
      })
      .catch(() => {
        warningThresholdLoaded = false
      })
  }

  // Only a deliberate renewal clears the warning. If an ordinary response could
  // clear it, moving the mouse toward the dialog would dismiss it and the
  // timeout would never fire for anyone sitting at their desk.
  if (seconds > warningSeconds.value && !isWarning.value) {
    isWarning.value = false
  }
}

export const resetSessionCountdown = () => {
  secondsRemaining.value = null
  isWarning.value = false
  hasExpired.value = false
  lastHeartbeatAt = 0
  warningThresholdLoaded = false
}

export const useSession = () => {
  const { lastActive } = useIdle(IDLE_AFTER_MS)
  const visibility = useDocumentVisibility()

  const sendHeartbeat = async () => {
    lastHeartbeatAt = Date.now()

    await service.heartbeat()
  }

  const tick = () => {
    if (secondsRemaining.value === null) return

    secondsRemaining.value = Math.max(secondsRemaining.value - 1, 0)

    if (secondsRemaining.value <= warningSeconds.value) {
      isWarning.value = true
    }

    if (secondsRemaining.value === 0) {
      hasExpired.value = true

      return
    }

    // Suspended while the warning shows: that is what makes the renew button
    // mean something rather than being dismissed by a passing cursor.
    if (isWarning.value) return

    const interactedRecently = Date.now() - lastActive.value < IDLE_AFTER_MS
    const heartbeatIsDue = Date.now() - lastHeartbeatAt >= HEARTBEAT_INTERVAL_MS

    if (interactedRecently && heartbeatIsDue) {
      sendHeartbeat().catch(() => undefined)
    }
  }

  const renew = async () => {
    isRenewing.value = true

    try {
      await sendHeartbeat()
      isWarning.value = false
    } catch {
      return
    } finally {
      isRenewing.value = false
    }
  }

  /**
   * A local timer means nothing after the lid has been shut for twenty minutes,
   * so reconcile on wake - through the read that does not count as activity,
   * because opening a laptop must not renew a session.
   */
  const resync = async () => {
    const response = await service.status().catch(() => undefined)

    if (response?.data?.warningMinutes) {
      warningSeconds.value = response.data.warningMinutes * 60
    }
  }

  // Guarded because every consumer calls useSession(); the interval and the
  // watcher should exist once.
  if (!started) {
    started = true

    useIntervalFn(tick, 1000)

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    const onVisible = () => {
      if (visibility.value === 'visible' && secondsRemaining.value !== null) {
        resync()
      }
    }

    document.addEventListener('visibilitychange', onVisible)
  }

  return {
    secondsRemaining: computed(() => secondsRemaining.value),
    isWarning: computed(() => isWarning.value),
    isRenewing: computed(() => isRenewing.value),
    hasExpired: computed(() => hasExpired.value),
    showCountdown: computed(
      () =>
        secondsRemaining.value !== null &&
        secondsRemaining.value <= COUNTDOWN_VISIBLE_FROM,
    ),
    renew,
    resync,
  }
}
